import { onMounted, onUnmounted } from 'vue';

/**
 * Composable to handle viewport resize events
 * Updates CSS custom property --vh to handle viewport height changes
 * This fixes issues with 100vh not updating properly on some browsers
 */
export function useViewportResize(): { updateViewportHeight: () => void } {
  let resizeTimeout: number | null = null;

  const updateViewportHeight = (): void => {
    // Clear any pending timeout
    if (resizeTimeout !== null) {
      clearTimeout(resizeTimeout);
    }

    // Debounce the resize event to avoid excessive calculations
    resizeTimeout = window.setTimeout(() => {
      // Calculate 1% of viewport height
      const vh = window.innerHeight * 0.01;

      // Set CSS custom property --vh on document root
      document.documentElement.style.setProperty('--vh', `${vh}px`);

      resizeTimeout = null;
    }, 100) as unknown as number;
  };

  onMounted(() => {
    // Set initial value
    updateViewportHeight();

    // Listen for resize events
    window.addEventListener('resize', updateViewportHeight);

    // Also listen for orientation changes on mobile devices
    window.addEventListener('orientationchange', updateViewportHeight);
  });

  onUnmounted(() => {
    // Clean up event listeners
    window.removeEventListener('resize', updateViewportHeight);
    window.removeEventListener('orientationchange', updateViewportHeight);

    // Clear any pending timeout
    if (resizeTimeout !== null) {
      clearTimeout(resizeTimeout);
    }
  });

  return {
    updateViewportHeight
  };
}
